import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Container,
  Fade,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { questions, translations, Language } from '../data/lead-qualifier-questions';
import {
  trackQuizLandingView,
  trackQuizStart,
  trackQuizQuestionView,
  trackQuizQuestionAnswered,
  trackQuizMilestone,
  trackQuizContactFormView,
  trackQuizFormFieldFocus,
  trackQuizPrivacyPolicyView,
  trackQuizPrivacyConsent,
  trackQuizFormSubmitAttempt,
  trackQuizFormError,
  trackQuizComplete,
  trackQuizGiftDownload,
  trackQuizStart360,
  trackQuizFinish,
  trackQuizLanguageToggle,
  trackQuizBackButton,
  trackCampaignSource,
  trackCTAClick,
  setQuizType,
  setUserId,
} from '../utils/analytics';

// Capture and store campaign params from URL (email campaigns, social ads, etc.)
const captureCampaignParams = () => {
  const params = new URLSearchParams(window.location.search);
  const email = params.get('uemail');
  const campaign = params.get('utm_campaign');
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  const content = params.get('utm_content');
  const term = params.get('utm_term');

  // Store if we have campaign data
  if (email || campaign || source) {
    if (email) {
      sessionStorage.setItem('campaign_email', email);
      setUserId(email);
    }
    if (campaign) sessionStorage.setItem('utm_campaign', campaign);
    if (source) sessionStorage.setItem('utm_source', source);
    if (medium) sessionStorage.setItem('utm_medium', medium);
    if (content) sessionStorage.setItem('utm_content', content);
    if (term) sessionStorage.setItem('utm_term', term);

    // Track campaign visit in GA
    trackCampaignSource({
      email: email || undefined,
      campaign: campaign || undefined,
      source: source || undefined,
      medium: medium || undefined
    });

    // Clean email from URL (keep UTM params for GA)
    if (email) {
      params.delete('uemail');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }
};

// Progress Stepper Component
const ProgressStepper: React.FC<{
  currentStep: 0 | 1 | 2;
  language: Language;
}> = ({ currentStep, language }) => {
  const t = translations[language];
  const isRTL = language === 'he';
  const steps = [t.stepStart, t.stepQuestions, t.stepFinish];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 2,
        px: 3,
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      {steps.map((label, index) => {
        const isCompleted = index < currentStep;
        const isActive = index === currentStep;
        const isLast = index === steps.length - 1;

        return (
          <React.Fragment key={index}>
            {/* Step circle */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isCompleted ? '#0693e3' : isActive ? '#fff' : '#fff',
                  border: isActive ? '2px solid #0693e3' : isCompleted ? 'none' : '2px solid #e5e7eb',
                  color: isCompleted ? '#fff' : isActive ? '#0693e3' : '#9ca3af',
                  transition: 'all 0.2s ease',
                }}
              >
                {isCompleted ? (
                  <CheckIcon sx={{ fontSize: 18 }} />
                ) : (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: isActive ? '#0693e3' : 'transparent',
                    }}
                  />
                )}
              </Box>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: '0.75rem',
                  color: isActive || isCompleted ? '#0693e3' : '#9ca3af',
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {label}
              </Typography>
            </Box>

            {/* Connector line */}
            {!isLast && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  bgcolor: isCompleted ? '#0693e3' : '#e5e7eb',
                  mx: 1,
                  mt: -2,
                  maxWidth: 80,
                  transition: 'all 0.2s ease',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// Single Select Option Component
const SingleSelectOption: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
  isRTL: boolean;
}> = ({ label, selected, onClick, isRTL }) => (
  <Button
    fullWidth
    onClick={onClick}
    sx={{
      py: 2,
      px: 3,
      mb: 1.5,
      justifyContent: 'space-between',
      flexDirection: isRTL ? 'row-reverse' : 'row',
      textAlign: isRTL ? 'right' : 'left',
      fontSize: '1rem',
      fontWeight: 400,
      borderRadius: '12px',
      border: selected ? '2px solid #0693e3' : '2px solid #e5e7eb',
      bgcolor: selected ? '#eff6ff' : '#fff',
      color: '#1f2937',
      textTransform: 'none',
      lineHeight: 1.5,
      '&:hover': {
        bgcolor: selected ? '#dbeafe' : '#f9fafb',
        borderColor: selected ? '#0693e3' : '#d1d5db',
      },
    }}
  >
    <Typography sx={{ flex: 1, textAlign: isRTL ? 'right' : 'left', px: 1 }}>{label}</Typography>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        border: selected ? '2px solid #0693e3' : '2px solid #d1d5db',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ml: isRTL ? 0 : 3,
        mr: isRTL ? 3 : 0,
        flexShrink: 0,
      }}
    >
      {selected && (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: '#0693e3',
          }}
        />
      )}
    </Box>
  </Button>
);

// Multi Select Option Component
const MultiSelectOption: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
  isRTL: boolean;
}> = ({ label, selected, onClick, isRTL }) => (
  <Button
    fullWidth
    onClick={onClick}
    sx={{
      py: 2,
      px: 3,
      mb: 1.5,
      justifyContent: 'space-between',
      flexDirection: isRTL ? 'row-reverse' : 'row',
      textAlign: isRTL ? 'right' : 'left',
      fontSize: '1rem',
      fontWeight: 400,
      borderRadius: '12px',
      border: selected ? '2px solid #0693e3' : '2px solid #e5e7eb',
      bgcolor: selected ? '#eff6ff' : '#fff',
      color: '#1f2937',
      textTransform: 'none',
      lineHeight: 1.5,
      '&:hover': {
        bgcolor: selected ? '#dbeafe' : '#f9fafb',
        borderColor: selected ? '#0693e3' : '#d1d5db',
      },
    }}
  >
    <Typography sx={{ flex: 1, textAlign: isRTL ? 'right' : 'left', px: 1 }}>{label}</Typography>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '4px',
        border: selected ? '2px solid #0693e3' : '2px solid #d1d5db',
        bgcolor: selected ? '#0693e3' : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ml: isRTL ? 0 : 3,
        mr: isRTL ? 3 : 0,
        flexShrink: 0,
      }}
    >
      {selected && <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />}
    </Box>
  </Button>
);

const QuizLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState<Language>('he');
  const [currentStep, setCurrentStep] = useState<'landing' | number | 'contact' | 'done'>('landing');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [multiSelectTemp, setMultiSelectTemp] = useState<string[]>([]);
  const [textInputTemp, setTextInputTemp] = useState('');
  const [otherInputTemp, setOtherInputTemp] = useState('');

  // Track milestones already fired
  const firedMilestones = useRef<Set<number>>(new Set());
  // Track if campaign params have been captured
  const campaignCapturedRef = useRef(false);

  const isRTL = language === 'he';
  const t = translations[language];

  // Track landing page view and capture campaign params on mount
  useEffect(() => {
    // Claim the quiz type for this page. It is also the default, but /ready
    // sets its own, and a visitor who goes there first and then comes here
    // would otherwise be counted in the readiness funnel.
    setQuizType('lead_qualifier');

    // Capture UTM params from URL (email, facebook, youtube, instagram, etc.)
    if (!campaignCapturedRef.current) {
      campaignCapturedRef.current = true;
      captureCampaignParams();
    }
    trackQuizLandingView(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Filter questions based on conditions
  const visibleQuestions = useMemo(() => {
    return questions.filter((q) => {
      if (!q.condition) return true;
      return q.condition(answers);
    });
  }, [answers]);

  const currentQuestionIndex = typeof currentStep === 'number' ? currentStep : -1;
  const currentQuestion = currentQuestionIndex >= 0 ? visibleQuestions[currentQuestionIndex] : null;

  // Track question/step changes
  useEffect(() => {
    if (currentStep === 'contact') {
      trackQuizContactFormView(language);
    } else if (typeof currentStep === 'number' && visibleQuestions[currentStep]) {
      const q = visibleQuestions[currentStep];
      trackQuizQuestionView(
        q.questionNumber,
        q.id,
        q.type,
        visibleQuestions.length,
        currentStep,
        language
      );

      // Check for milestones (25%, 50%, 75%)
      const progress = Math.round(((currentStep + 1) / visibleQuestions.length) * 100);
      const milestones = [25, 50, 75];
      milestones.forEach(milestone => {
        if (progress >= milestone && !firedMilestones.current.has(milestone)) {
          firedMilestones.current.add(milestone);
          trackQuizMilestone(milestone, visibleQuestions.length, language);
        }
      });
    }
  }, [currentStep, visibleQuestions, language]);

  // Calculate progress step for stepper
  const getProgressStep = (): 0 | 1 | 2 => {
    if (currentStep === 'landing') return 0;
    if (currentStep === 'done') return 2;
    if (currentStep === 'contact') return 1;
    return 1;
  };

  const transition = (callback: () => void) => {
    setIsTransitioning(true);
    setTimeout(() => {
      callback();
      setIsTransitioning(false);
    }, 150);
  };

  const goNext = () => {
    if (currentStep === 'landing') {
      trackCTAClick('quiz_start_button', language);
      trackQuizStart(language);
      transition(() => setCurrentStep(0));
    } else if (typeof currentStep === 'number') {
      const nextIndex = currentStep + 1;
      if (nextIndex >= visibleQuestions.length) {
        transition(() => setCurrentStep('contact'));
      } else {
        transition(() => {
          setCurrentStep(nextIndex);
          setMultiSelectTemp([]);
          setTextInputTemp('');
          setOtherInputTemp('');
        });
      }
    }
  };

  const goBack = () => {
    if (typeof currentStep === 'number' && currentStep > 0) {
      trackQuizBackButton(currentStep, currentStep - 1, language);
      transition(() => {
        setCurrentStep(currentStep - 1);
        setMultiSelectTemp([]);
        setTextInputTemp('');
        setOtherInputTemp('');
      });
    } else if (currentStep === 'contact') {
      trackQuizBackButton('contact', visibleQuestions.length - 1, language);
      transition(() => setCurrentStep(visibleQuestions.length - 1));
    }
  };

  const handleSingleSelect = (questionId: string, value: string) => {
    if (currentQuestion && typeof currentStep === 'number') {
      trackQuizQuestionAnswered(
        currentQuestion.questionNumber,
        questionId,
        'single-select',
        value,
        visibleQuestions.length,
        currentStep,
        language
      );
    }
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setTimeout(goNext, 200);
  };

  const handleMultiToggle = (value: string) => {
    setMultiSelectTemp((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleMultiSubmit = (questionId: string) => {
    if (multiSelectTemp.length === 0) return;
    const finalAnswers = otherInputTemp.trim()
      ? [...multiSelectTemp, `other:${otherInputTemp.trim()}`]
      : multiSelectTemp;
    if (currentQuestion && typeof currentStep === 'number') {
      trackQuizQuestionAnswered(
        currentQuestion.questionNumber,
        questionId,
        'multi-select',
        finalAnswers,
        visibleQuestions.length,
        currentStep,
        language
      );
    }
    setAnswers((prev) => ({ ...prev, [questionId]: finalAnswers }));
    goNext();
  };

  const handleTextSubmit = (questionId: string) => {
    if (!textInputTemp.trim()) {
      // For optional text questions, allow skipping
      if (!currentQuestion?.required) {
        goNext();
        return;
      }
      return;
    }
    if (currentQuestion && typeof currentStep === 'number') {
      trackQuizQuestionAnswered(
        currentQuestion.questionNumber,
        questionId,
        'text',
        textInputTemp.trim(),
        visibleQuestions.length,
        currentStep,
        language
      );
    }
    setAnswers((prev) => ({ ...prev, [questionId]: textInputTemp.trim() }));
    goNext();
  };

  const handleContactSubmit = async () => {
    const hasAllFields = !!(contactForm.firstName && contactForm.email && contactForm.phone);
    trackQuizFormSubmitAttempt(hasAllFields, privacyConsent, language);

    if (!contactForm.firstName || !contactForm.email || !contactForm.phone || !privacyConsent) return;

    // Validate email
    if (!isValidEmail(contactForm.email)) {
      trackQuizFormError('invalid_email', language);
      setEmailError(language === 'he' ? 'כתובת אימייל לא תקינה' : 'Invalid email address');
      return;
    }
    setEmailError(null);

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const submitQuizLead = httpsCallable(functions, 'submitQuizLead');
      await submitQuizLead({
        answers,
        contact: contactForm,
        language,
      });

      // Track 100% milestone and completion
      trackQuizMilestone(100, visibleQuestions.length, language);
      trackQuizComplete(visibleQuestions.length, Object.keys(answers).length, language);

      transition(() => setCurrentStep('done'));
    } catch (error) {
      console.error('Error submitting quiz:', error);
      trackQuizFormError('submit_error', language);
      setSubmitError(
        language === 'he' ? 'שגיאה בשליחה. נסה שוב.' : 'Error submitting. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStart360 = () => {
    trackCTAClick('quiz_start_360_button', language);
    trackQuizStart360(language, answers.q11_deep_analysis || 'unknown');
    // Save lead info to sessionStorage for 360 pre-fill
    sessionStorage.setItem('quiz_lead_name', `${contactForm.firstName} ${contactForm.lastName}`.trim());
    sessionStorage.setItem('quiz_lead_email', contactForm.email);
    sessionStorage.setItem('quiz_lead_phone', contactForm.phone);
    if (answers.q7b_team_size) {
      sessionStorage.setItem('quiz_lead_employees', answers.q7b_team_size);
    }
    navigate('/assessment/360');
  };

  // Text field style
  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      '& fieldset': { borderColor: '#e5e7eb', borderWidth: 2 },
      '&:hover fieldset': { borderColor: '#d1d5db' },
      '&.Mui-focused fieldset': { borderColor: '#0693e3' },
    },
    '& .MuiInputLabel-root': { color: '#9ca3af' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#0693e3' },
  };

  return (
    <Box
      sx={{ minHeight: '100vh', bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header with Logo and Progress Stepper */}
      {currentStep !== 'landing' && currentStep !== 'done' && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            bgcolor: '#fff',
            zIndex: 100,
            borderBottom: '1px solid #f3f4f6',
          }}
        >
          {/* Logo Header - hidden on mobile */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              justifyContent: 'center',
              py: 1.5,
              borderBottom: '1px solid #f3f4f6',
              bgcolor: '#ffffff',
            }}
          >
            <Box
              component="img"
              src="/federman-logo.jpg"
              alt="פדרמן יועצים"
              sx={{
                height: 40,
                width: 'auto',
                bgcolor: '#ffffff',
                p: 0.5,
                borderRadius: '4px',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
            <Button
              onClick={goBack}
              disabled={currentStep === 0}
              sx={{
                minWidth: 40,
                color: '#6b7280',
                '&:disabled': { color: '#e5e7eb' },
              }}
            >
              {isRTL ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </Button>
            <Box sx={{ flex: 1 }}>
              <ProgressStepper currentStep={getProgressStep()} language={language} />
            </Box>
            <Box sx={{ width: 40 }} /> {/* Spacer for balance */}
          </Box>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', flexDirection: 'column', py: { xs: 1, sm: 4 } }}>
          <Fade in={!isTransitioning} timeout={200}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Landing Page */}
              {currentStep === 'landing' && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: 'center',
                    px: 2,
                  }}
                >
                  {/* Federman Logo - hidden on mobile */}
                  <Box
                    component="img"
                    src="/federman-logo.jpg"
                    alt="פדרמן יועצים"
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      height: 60,
                      width: 'auto',
                      mx: 'auto',
                      mb: 3,
                      bgcolor: '#ffffff',
                      p: 1,
                      borderRadius: '8px',
                    }}
                  />
                  <ProgressStepper currentStep={0} language={language} />

                  <Typography
                    variant="h4" component="h1"
                    sx={{
                      fontWeight: 700,
                      mb: { xs: 1, sm: 2 },
                      mt: { xs: 2, sm: 4 },
                      color: '#111827',
                      lineHeight: 1.4,
                      fontSize: { xs: '1.4rem', sm: '2rem' },
                    }}
                  >
                    {t.landingTitle}
                  </Typography>

                  <Typography sx={{ color: '#6b7280', mb: { xs: 2, sm: 4 }, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                    {t.landingSubtitle}
                  </Typography>

                  {/* Guide Image */}
                  <Box
                    component="img"
                    src="/gift_image.png?v=2"
                    alt="מדריך מתנה"
                    sx={{
                      width: '100%',
                      maxWidth: { xs: 220, sm: 300 },
                      height: 'auto',
                      mx: 'auto',
                      mb: { xs: 2, sm: 4 },
                      borderRadius: '16px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    }}
                  />

                  {/* Timer */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: '#6b7280',
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 20, mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
                    <Typography sx={{ fontSize: '0.95rem' }}>{t.timerText}</Typography>
                  </Box>

                  {/* Tip */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      mb: 4,
                      color: '#6b7280',
                      px: 2,
                    }}
                  >
                    <LightbulbOutlinedIcon
                      sx={{
                        fontSize: 20,
                        mr: isRTL ? 0 : 1,
                        ml: isRTL ? 1 : 0,
                        mt: 0.3,
                        color: '#fbbf24',
                      }}
                    />
                    <Typography sx={{ fontSize: '0.9rem', textAlign: isRTL ? 'right' : 'left' }}>
                      {t.tipText}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={goNext}
                    sx={{
                      py: 2,
                      px: 6,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: '12px',
                      bgcolor: '#0693e3',
                      '&:hover': { bgcolor: '#0578b8' },
                      alignSelf: 'center',
                      width: '100%',
                      maxWidth: 300,
                    }}
                  >
                    {t.startButton}
                  </Button>

                  {/* Language Toggle */}
                  <Button
                    size="small"
                    onClick={() => {
                      const newLang = language === 'he' ? 'en' : 'he';
                      trackQuizLanguageToggle(language, newLang);
                      setLanguage(newLang);
                    }}
                    sx={{ mt: 3, color: '#9ca3af', fontSize: '0.85rem' }}
                  >
                    {language === 'he' ? 'Switch to English' : 'עברית'}
                  </Button>
                </Box>
              )}

              {/* Question Pages */}
              {typeof currentStep === 'number' && currentQuestion && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Question Text */}
                  <Typography
                    variant="h5" component="h1"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      color: '#111827',
                      lineHeight: 1.5,
                      textAlign: isRTL ? 'right' : 'left',
                    }}
                  >
                    {currentQuestion.questionNumber}. {currentQuestion.text[language]}
                    {currentQuestion.required && (
                      <Typography component="span" sx={{ color: '#ef4444', ml: 0.5 }}>
                        *
                      </Typography>
                    )}
                  </Typography>

                  {/* Single Select */}
                  {currentQuestion.type === 'single-select' && currentQuestion.options && (
                    <Box>
                      {currentQuestion.options.map((option) => (
                        <SingleSelectOption
                          key={option.value}
                          label={option.label[language]}
                          selected={answers[currentQuestion.id] === option.value}
                          onClick={() => handleSingleSelect(currentQuestion.id, option.value)}
                          isRTL={isRTL}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Multi Select */}
                  {currentQuestion.type === 'multi-select' && currentQuestion.options && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Box>
                        {currentQuestion.options.map((option) => (
                          <MultiSelectOption
                            key={option.value}
                            label={option.label[language]}
                            selected={multiSelectTemp.includes(option.value)}
                            onClick={() => handleMultiToggle(option.value)}
                            isRTL={isRTL}
                          />
                        ))}
                        {currentQuestion.hasOther && (
                          <>
                            <MultiSelectOption
                              label={t.other}
                              selected={multiSelectTemp.includes('other')}
                              onClick={() => handleMultiToggle('other')}
                              isRTL={isRTL}
                            />
                            {multiSelectTemp.includes('other') && (
                              <TextField
                                fullWidth
                                placeholder={language === 'he' ? 'פרט...' : 'Please specify...'}
                                value={otherInputTemp}
                                onChange={(e) => setOtherInputTemp(e.target.value)}
                                sx={{ ...textFieldStyle, mb: 1.5 }}
                              />
                            )}
                          </>
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }} />
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleMultiSubmit(currentQuestion.id)}
                        disabled={multiSelectTemp.length === 0}
                        sx={{
                          py: 2,
                          mt: 3,
                          fontSize: '1.05rem',
                          fontWeight: 600,
                          borderRadius: '12px',
                          bgcolor: '#0693e3',
                          '&:hover': { bgcolor: '#0578b8' },
                          '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
                        }}
                      >
                        {t.nextButton}
                      </Button>
                    </Box>
                  )}

                  {/* Text Input */}
                  {currentQuestion.type === 'text' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={textInputTemp}
                        onChange={(e) => setTextInputTemp(e.target.value)}
                        sx={{
                          ...textFieldStyle,
                          '& .MuiOutlinedInput-root': {
                            ...textFieldStyle['& .MuiOutlinedInput-root'],
                            fontSize: '1.05rem',
                          },
                        }}
                      />
                      <Box sx={{ flex: 1 }} />
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleTextSubmit(currentQuestion.id)}
                        disabled={currentQuestion.required && !textInputTemp.trim()}
                        sx={{
                          py: 2,
                          mt: 3,
                          fontSize: '1.05rem',
                          fontWeight: 600,
                          borderRadius: '12px',
                          bgcolor: '#0693e3',
                          '&:hover': { bgcolor: '#0578b8' },
                          '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
                        }}
                      >
                        {t.nextButton}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Contact Form */}
              {currentStep === 'contact' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Typography
                    variant="h5" component="h1"
                    sx={{
                      fontWeight: 600,
                      mb: 4,
                      color: '#111827',
                      lineHeight: 1.4,
                      textAlign: isRTL ? 'right' : 'left',
                    }}
                  >
                    {t.contactTitle}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label={`${t.firstName} *`}
                      value={contactForm.firstName}
                      onChange={(e) => setContactForm((f) => ({ ...f, firstName: e.target.value }))}
                      onFocus={() => trackQuizFormFieldFocus('first_name', language)}
                      sx={textFieldStyle}
                    />
                    <TextField
                      label={`${t.lastName} *`}
                      value={contactForm.lastName}
                      onChange={(e) => setContactForm((f) => ({ ...f, lastName: e.target.value }))}
                      onFocus={() => trackQuizFormFieldFocus('last_name', language)}
                      sx={textFieldStyle}
                    />
                    <TextField
                      label={`${t.email} *`}
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => {
                        setContactForm((f) => ({ ...f, email: e.target.value }));
                        if (emailError) setEmailError(null);
                      }}
                      onFocus={() => trackQuizFormFieldFocus('email', language)}
                      error={!!emailError}
                      helperText={emailError}
                      sx={{
                        ...textFieldStyle,
                        '& .MuiFormHelperText-root': {
                          textAlign: isRTL ? 'right' : 'left',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <Box
                            component="span"
                            sx={{ mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0, color: '#9ca3af' }}
                          >
                            ✉️
                          </Box>
                        ),
                      }}
                    />
                    <TextField
                      label={`${t.phone} *`}
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))}
                      onFocus={() => trackQuizFormFieldFocus('phone', language)}
                      sx={textFieldStyle}
                      InputProps={{
                        startAdornment: (
                          <Box
                            component="span"
                            sx={{
                              mr: isRTL ? 0 : 1,
                              ml: isRTL ? 1 : 0,
                              display: 'flex',
                              alignItems: 'center',
                              color: '#6b7280',
                            }}
                          >
                            🇮🇱
                          </Box>
                        ),
                      }}
                    />

                    {/* Privacy Consent */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={privacyConsent}
                          onChange={(e) => {
                            trackQuizPrivacyConsent(e.target.checked, language);
                            setPrivacyConsent(e.target.checked);
                          }}
                          sx={{
                            color: '#d1d5db',
                            '&.Mui-checked': { color: '#0693e3' },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {t.privacyConsent.split('למדיניות הפרטיות')[0]}
                          <Link
                            component="button"
                            type="button"
                            onClick={(e: React.MouseEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              trackQuizPrivacyPolicyView(language);
                              setPrivacyModalOpen(true);
                            }}
                            sx={{ color: '#0693e3', cursor: 'pointer', verticalAlign: 'baseline' }}
                          >
                            {t.privacyPolicy}
                          </Link>
                          {t.privacyConsent.split('למדיניות הפרטיות')[1] || ''}
                        </Typography>
                      }
                      sx={{
                        alignItems: 'flex-start',
                        mt: 1,
                        '& .MuiFormControlLabel-label': { mt: 0.5 },
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }} />

                  {submitError && (
                    <Typography sx={{ color: '#ef4444', mb: 2, textAlign: 'center' }}>
                      {submitError}
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleContactSubmit}
                    disabled={
                      !contactForm.firstName ||
                      !contactForm.email ||
                      !contactForm.phone ||
                      !privacyConsent ||
                      isSubmitting
                    }
                    sx={{
                      py: 2,
                      mt: 3,
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      borderRadius: '12px',
                      bgcolor: '#0693e3',
                      '&:hover': { bgcolor: '#0578b8' },
                      '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t.nextButton}
                  </Button>
                </Box>
              )}

              {/* Done Screen */}
              {currentStep === 'done' && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: 'center',
                    px: 2,
                  }}
                >
                  {/* Federman Logo */}
                  <Box
                    component="img"
                    src="/federman-logo.jpg"
                    alt="פדרמן יועצים"
                    sx={{
                      height: 50,
                      width: 'auto',
                      mx: 'auto',
                      mb: 4,
                      bgcolor: '#ffffff',
                      p: 1,
                      borderRadius: '8px',
                    }}
                  />
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: 40, color: '#22c55e' }} />
                  </Box>

                  <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2, color: '#111827' }}>
                    {t.thankYouTitle}
                  </Typography>

                  <Typography sx={{ color: '#6b7280', mb: 4, fontSize: '1.1rem' }}>
                    {t.thankYouSubtitle}
                  </Typography>

                  {/* Download Gift Button */}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      trackCTAClick('quiz_gift_download_button', language);
                      trackQuizGiftDownload(language);
                      window.open('/productivity_gift.pdf?v=2', '_blank');
                    }}
                    sx={{
                      py: 2,
                      px: 4,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      borderRadius: '12px',
                      bgcolor: '#f97316',
                      '&:hover': { bgcolor: '#ea580c' },
                      mb: 3,
                    }}
                  >
                    {language === 'he' ? '📥 להורדת המדריך שלך' : '📥 Download Your Guide'}
                  </Button>

                  {/* Options based on user answer to Q11 */}
                  {(answers.q11_deep_analysis === 'yes' || answers.q11_deep_analysis === 'maybe') && (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleStart360}
                      sx={{
                        py: 2,
                        px: 4,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: '12px',
                        bgcolor: '#0693e3',
                        '&:hover': { bgcolor: '#0578b8' },
                        mb: 2,
                      }}
                    >
                      {t.start360Button}
                    </Button>
                  )}

                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      trackQuizFinish(language);
                      window.location.href = 'https://federman.org.il';
                    }}
                    sx={{
                      py: 2,
                      px: 4,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: '12px',
                      borderColor: '#e5e7eb',
                      color: '#6b7280',
                      '&:hover': {
                        borderColor: '#d1d5db',
                        bgcolor: '#f9fafb',
                      },
                    }}
                  >
                    {t.finishButton}
                  </Button>
                </Box>
              )}
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Privacy Policy Modal */}
      <Dialog
        open={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            direction: isRTL ? 'rtl' : 'ltr',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#0693e3',
            color: '#fff',
            fontWeight: 700,
          }}
        >
          {language === 'he' ? 'מדיניות פרטיות' : 'Privacy Policy'}
          <IconButton
            onClick={() => setPrivacyModalOpen(false)}
            sx={{ color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {language === 'he' ? (
            <Box sx={{ textAlign: 'right', lineHeight: 1.8 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#32373c' }}>
                מדיניות פרטיות ותנאי שימוש
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>איסוף מידע:</strong> אנו אוספים את המידע שאתה מספק לנו בטופס זה, כולל שם, כתובת דוא"ל ומספר טלפון.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>שימוש במידע:</strong> המידע ישמש אותנו ליצירת קשר עמך, לשליחת תוצאות השאלון, ולהצעת שירותים ומוצרים רלוונטיים. אנו עשויים לפנות אליך באמצעות דוא"ל, טלפון, SMS, וואטסאפ או כל אמצעי תקשורת אחר לצורכי שיווק, מכירות ותקשורת עסקית.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>תקשורת שיווקית:</strong> בעצם מילוי הטופס, הנך מאשר/ת קבלת תוכן שיווקי, הצעות מכירה, עדכונים, מבצעים וחומרים פרסומיים מאתנו בכל אמצעי התקשורת שסיפקת.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>אבטחת מידע:</strong> אנו נוקטים באמצעי אבטחה מקובלים לשמירה על המידע שלך.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>הסרה מרשימת התפוצה:</strong> תוכל להסיר את עצמך מרשימת התפוצה שלנו בכל עת על ידי פנייה אלינו או לחיצה על קישור ההסרה בהודעות שנשלח.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>יצירת קשר:</strong> לכל שאלה בנוגע למדיניות הפרטיות, ניתן לפנות אלינו באמצעות האתר federman.org.il
              </Typography>

              <Typography variant="body2" sx={{ mt: 3, color: '#374151', fontSize: '0.9rem', fontWeight: 500 }}>
                בעצם מילוי הטופס וסימון תיבת האישור, הנך מאשר/ת שקראת והבנת את התנאים לעיל ומסכים/ה לקבלת פניות שיווקיות ומכירתיות מפדרמן יועצים.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'left', lineHeight: 1.8 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#32373c' }}>
                Privacy Policy & Terms
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>Information Collection:</strong> We collect the information you provide in this form, including your name, email address, and phone number.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>Use of Information:</strong> We will use this information to contact you, send you questionnaire results, and offer relevant services and products. We may contact you via email, phone, SMS, WhatsApp, or any other communication method for marketing, sales, and business communications.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>Marketing Communications:</strong> By submitting this form, you consent to receive marketing content, sales offers, updates, promotions, and advertising materials from us through all communication channels you have provided.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>Data Security:</strong> We take standard security measures to protect your information.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>Unsubscribe:</strong> You can remove yourself from our mailing list at any time by contacting us or clicking the unsubscribe link in our messages.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                <strong>Contact:</strong> For any questions regarding our privacy policy, please contact us through federman.org.il
              </Typography>

              <Typography variant="body2" sx={{ mt: 3, color: '#374151', fontSize: '0.9rem', fontWeight: 500 }}>
                By submitting this form and checking the consent box, you confirm that you have read and understood the terms above and agree to receive marketing and sales communications from Federman Consultants.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default QuizLandingPage;
